/**
 * Traduzione di un errore di rete in un messaggio per l'utente.
 *
 * Prima esisteva in tredici copie sparse in otto file, nella forma
 * `caught instanceof ApiError ? caught.message : '<ricaduta>'`. Una sola
 * funzione tiene la regola in un posto: il messaggio del backend passa così
 * com'è, tutto il resto ricade su una frase che dice anche cosa fare.
 *
 * Qui non si aggiungono dettagli tecnici: mai il codice HTTP, mai il percorso
 * della richiesta. Un messaggio del genere non aiuta chi legge e racconta a
 * chiunque guardi lo schermo com'è fatta la piattaforma.
 *
 * La ricaduta è una **funzione** e non una costante: una costante sarebbe
 * valutata al caricamento del modulo e resterebbe nella lingua di allora, anche
 * dopo che l'utente ne ha scelta un'altra.
 */

import { t } from '../i18n';
import { ApiError } from './client';

/**
 * Ricaduta usata quando l'errore non viene dal backend (rete, bug, timeout).
 *
 * @returns La frase nella lingua attiva.
 */
export function genericFailure(): string {
  return t('common.errors.generic');
}

/**
 * Restituisce il messaggio da mostrare per un errore intercettato.
 *
 * @param caught Errore intercettato in un `catch`.
 * @param fallback Frase da usare quando l'errore non viene dal backend.
 * @returns Il messaggio, già pronto per un `Alert` o un `message.error`.
 */
export function describeApiError(caught: unknown, fallback?: string): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return fallback ?? genericFailure();
}

/**
 * Sostituisce il messaggio di un rifiuto per dati non validi (422) con uno
 * contestuale.
 *
 * Il backend risponde ai 422 sempre con lo stesso messaggio generico: il
 * motivo resta nei suoi log. Dove la pagina sa quale regola può essere stata
 * violata, lo dice lei con parole che spiegano che cosa fare.
 *
 * @param task Operazione da eseguire.
 * @param message Messaggio da mostrare se il backend rifiuta con 422.
 * @returns Il risultato dell'operazione.
 */
export async function explainRefusal<T>(task: () => Promise<T>, message: string): Promise<T> {
  try {
    return await task();
  } catch (caught) {
    if (caught instanceof ApiError && caught.status === 422) {
      throw new ApiError(422, message);
    }
    throw caught;
  }
}

/**
 * Indica se un errore è un rifiuto per mancanza di permessi.
 *
 * Serve alle sezioni che su 403 non mostrano un errore ma spiegano che cosa
 * servirebbe: un 403 atteso non è un guasto.
 *
 * @param caught Errore intercettato in un `catch`.
 * @returns True se il backend ha risposto 403.
 */
export function isForbidden(caught: unknown): boolean {
  return caught instanceof ApiError && caught.isForbidden;
}

/**
 * Sostituisce il messaggio di un rifiuto con codice di dominio con uno scritto
 * dalla pagina.
 *
 * `agents` e `tools` rispondono 409 con `{"detail", "code"}` alle regole che la
 * pagina deve distinguere (ADR 0008, Appendice A). Il `detail` è pensato per i
 * log, in inglese; la pagina conosce il contesto e spiega con parole sue che
 * cosa fare. Un codice che la pagina non conosce passa com'è.
 *
 * @param task Operazione da eseguire.
 * @param messages Messaggio da mostrare per ciascun codice atteso.
 * @returns Il risultato dell'operazione.
 */
export async function explainCodes<T>(
  task: () => Promise<T>,
  messages: Readonly<Record<string, string>>,
): Promise<T> {
  try {
    return await task();
  } catch (caught) {
    if (caught instanceof ApiError && caught.code !== null) {
      const message = messages[caught.code];
      if (message !== undefined) {
        throw new ApiError(caught.status, message, caught.retryAfterSeconds, caught.code);
      }
    }
    throw caught;
  }
}

/**
 * Indica se un errore è un conflitto (409): la risorsa esiste già o una regola
 * di dominio lo impedisce.
 *
 * @param caught Errore intercettato in un `catch`.
 * @returns True se il backend ha risposto 409.
 */
export function isConflict(caught: unknown): boolean {
  return caught instanceof ApiError && caught.status === 409;
}
